/**
 * iter84.56.8 — Tech-debt split from ReleaseOpsAuditCards.jsx.
 * Mechanical extraction; no behavior change.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    CheckCircle2, 
    Loader2, ClipboardCheck, 
    
} from "lucide-react";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";

export function AuditAcknowledgementLogCard({ environment = "preview" }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [includeRevoked, setIncludeRevoked] = useState(false);

    const fetchAcks = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.releaseOpsListAuditAcks(includeRevoked);
            setItems(r.items || []);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.detail || "Failed to load acks");
        } finally {
            setLoading(false);
        }
    }, [includeRevoked]);

    useEffect(() => { fetchAcks(); }, [fetchAcks]);

    const revoke = async (id) => {
        try {
            await api.releaseOpsRevokeAuditAck(id);
            toast.success("Revoked.");
            await fetchAcks();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Revoke failed");
        }
    };

    const active = items.filter((a) => !a.revoked_at);
    const revoked = items.filter((a) => a.revoked_at);

    const badge = `${active.length} active${includeRevoked ? ` · ${revoked.length} revoked` : ""}`;
    const badgeClass = active.length > 0
        ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/40"
        : "bg-zinc-700/40 text-zinc-400 border-zinc-600/40";

    const toggleBtn = (
        <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); setIncludeRevoked((v) => !v); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); setIncludeRevoked((v) => !v); } }}
            data-testid="audit-ack-log-toggle-revoked"
            className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
        >
            {includeRevoked ? "Hide revoked" : "Show revoked"}
        </span>
    );

    return (
        <CollapsibleCard
            testId="audit-ack-log-card"
            icon={ClipboardCheck}
            title="Acknowledgement log"
            badge={badge}
            badgeClass={badgeClass}
            headerExtra={toggleBtn}
        >
            <div data-environment={environment} data-active-count={active.length}>
                <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
                    Operator-accepted audit issues. Each ack is bound to a state signature — if the underlying state changes (e.g., orphan count grows), the ack auto-invalidates and the Parity Sweep row re-appears as FAIL.
                </p>
                {loading && (
                    <p className="text-xs text-zinc-500 italic flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                    </p>
                )}
                {error && (
                    <p className="text-xs text-rose-400" data-testid="audit-ack-log-error">
                        {error}
                    </p>
                )}
                {!loading && !error && items.length === 0 && (
                    <p data-testid="audit-ack-log-empty" className="text-[11px] text-zinc-500 italic flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        No acknowledgements{includeRevoked ? " (active or revoked)" : " active"}.
                    </p>
                )}
                {!loading && !error && items.length > 0 && (
                    <ul className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="audit-ack-log-rows">
                        {items.map((a) => (
                            <li
                                key={a.id}
                                data-testid={`audit-ack-log-row-${a.id}`}
                                data-revoked={a.revoked_at ? "true" : "false"}
                                className={`border rounded-sm p-2 text-[11px] ${
                                    a.revoked_at
                                        ? "border-zinc-700 bg-zinc-900/40 opacity-60"
                                        : "border-cyan-500/30 bg-cyan-500/5"
                                }`}
                            >
                                <div className="flex items-center gap-2 flex-wrap">
                                    <code className={`text-[10px] px-1 rounded ${a.revoked_at ? "bg-zinc-800 text-zinc-400" : "bg-cyan-500/20 text-cyan-200"}`}>
                                        {a.check_id}
                                    </code>
                                    <span className={`text-[9px] font-mono ${a.revoked_at ? "text-zinc-500" : "text-cyber"}`}>
                                        sig:{a.signature}
                                    </span>
                                    {a.revoked_at && (
                                        <span className="text-[9px] text-rose-300 font-bold uppercase tracking-widest">Revoked</span>
                                    )}
                                    {!a.revoked_at && (
                                        <button
                                            type="button"
                                            onClick={() => revoke(a.id)}
                                            data-testid={`audit-ack-log-row-${a.id}-revoke`}
                                            className="ml-auto text-[10px] text-zinc-400 hover:text-rose-300 hover:underline shrink-0 uppercase tracking-widest"
                                        >
                                            Revoke
                                        </button>
                                    )}
                                </div>
                                <p className="text-[11px] text-zinc-200 mt-1 italic">"{a.reason}"</p>
                                <p className="text-[9px] text-zinc-500 mt-1 font-mono">
                                    by {a.acked_by} · {new Date(a.acked_at).toLocaleString()}
                                    {a.revoked_at && (
                                        <span> · revoked {new Date(a.revoked_at).toLocaleString()}</span>
                                    )}
                                </p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </CollapsibleCard>
    );
}
